import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usersAPI, vaultAPI } from '../../services/api';
import { fmtDate } from '../../utils/dateUtils';
import { parseApiError } from '../../utils/errors';
import VaultRevealModal from '../../components/VaultRevealModal';

const CATEGORIES = ['Social Media', 'Email', 'Banking', 'Internal Tool', 'Hosting', 'Vendor', 'Other'];
const SPECIAL = '!@#$%^&*()-_=+?';
const EMPTY = { title: '', password: '', category: 'Social Media', account_identifier: '', url: '', notes: '' };

function generatePassword(length = 18) {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789' + SPECIAL;
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

function strengthOf(pw) {
  if (!pw) return null;
  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { label: 'Weak', color: 'red' };
  if (score === 3) return { label: 'Fair', color: 'orange' };
  if (score === 4) return { label: 'Strong', color: 'blue' };
  return { label: 'Very strong', color: 'green' };
}

/** Super-admin control panel for the password bank. */
export default function AdminVault() {
  const [entries, setEntries] = useState([]);
  const [people, setPeople] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // entry being edited, or null for a new one
  const [form, setForm] = useState(EMPTY);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const [managing, setManaging] = useState(null);  // entry whose access is being managed
  const [grants, setGrants] = useState([]);
  const [picked, setPicked] = useState([]);
  const [expiry, setExpiry] = useState('');
  const [revealing, setRevealing] = useState(null);

  const [logEntry, setLogEntry] = useState(null);  // entry whose log is open
  const [logs, setLogs] = useState([]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const load = useCallback(() => {
    setLoading(true);
    vaultAPI.list(search.trim() ? { search: search.trim() } : {})
      .then(({ data }) => setEntries(data))
      .catch((e) => setError(parseApiError(e, 'Failed to load the password bank')))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    vaultAPI.status().then(({ data }) => setConfigured(data.configured)).catch(() => {});
    usersAPI.list({ is_active: true }).then(({ data }) => setPeople(data)).catch(() => {});
  }, []);

  // ---- create / edit ----------------------------------------------------

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowPw(false); setShowForm(true); };
  const openEdit = (entry) => {
    setEditing(entry);
    // The password is deliberately absent — leaving it blank keeps the stored
    // secret untouched, so a metadata edit cannot wipe it.
    setForm({
      title: entry.title, password: '', category: entry.category || '',
      account_identifier: entry.account_identifier || '', url: entry.url || '',
      notes: entry.notes || '',
    });
    setShowPw(false);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!editing && !form.password) { setError('A password is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category || null,
        account_identifier: form.account_identifier || null,
        url: form.url || null,
        notes: form.notes || null,
      };
      if (form.password) payload.password = form.password;
      if (editing) {
        await vaultAPI.update(editing.id, payload);
        flash(form.password ? 'Password rotated — everyone with access was notified' : 'Details updated');
      } else {
        await vaultAPI.create(payload);
        flash('Password stored');
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      setError(parseApiError(err, 'Could not save this entry'));
    } finally { setSaving(false); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete "${entry.title}"? Everyone's access to it is revoked immediately.`)) return;
    try { await vaultAPI.remove(entry.id); flash('Entry deleted'); load(); }
    catch (err) { setError(parseApiError(err, 'Delete failed')); }
  };

  // ---- access management ------------------------------------------------

  const openAccess = async (entry) => {
    setManaging(entry);
    setPicked([]);
    setExpiry('');
    try { const { data } = await vaultAPI.grants(entry.id); setGrants(data); }
    catch (err) { setError(parseApiError(err, 'Could not load access list')); }
  };

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const saveGrants = async () => {
    if (picked.length === 0) return;
    try {
      const { data } = await vaultAPI.grant(managing.id, {
        user_ids: picked,
        expires_at: expiry ? new Date(expiry).toISOString() : null,
      });
      setGrants(data);
      setPicked([]);
      flash('Access granted');
      load();
    } catch (err) { setError(parseApiError(err, 'Could not grant access')); }
  };

  const revoke = async (grant) => {
    try {
      await vaultAPI.revoke(grant.id);
      setGrants((g) => g.filter((x) => x.id !== grant.id));
      flash('Access revoked');
      load();
    } catch (err) { setError(parseApiError(err, 'Could not revoke access')); }
  };

  const openLog = async (entry) => {
    setLogEntry(entry);
    try { const { data } = await vaultAPI.accessLog({ entry_id: entry.id, limit: 100 }); setLogs(data); }
    catch (err) { setError(parseApiError(err, 'Could not load the access log')); }
  };

  const granted = new Set(grants.map((g) => g.user_id));
  const strength = strengthOf(form.password);

  return (
    <div className="page">
      <div className="page-header">
        <h2>🔐 Password Bank — Management</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/vault" className="btn btn-ghost">My view</Link>
          <button className="btn btn-primary" onClick={openNew}>+ Add password</button>
        </div>
      </div>

      {!configured && (
        <div className="alert alert-warning">
          <strong>The password bank is not active.</strong> Generate a key with{' '}
          <code>python -m app.manage generate-vault-key</code>, put it in the
          server environment as <code>VAULT_MASTER_KEY</code>, and restart. Back
          the key up off the server — without it, stored passwords cannot be recovered.
        </div>
      )}
      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {showForm && (
        <form className="form-card mb-4" onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input required maxLength={150} value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Company Instagram" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="">None</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Username / User ID</label>
              <input maxLength={200} value={form.account_identifier}
                onChange={(e) => setForm({ ...form, account_identifier: e.target.value })}
                placeholder="Login name for this account" />
            </div>
            <div className="form-group">
              <label>Link</label>
              <input type="url" maxLength={500} value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://…" />
            </div>
          </div>

          <div className="form-group">
            <label>Password {editing ? '(leave blank to keep the current one)' : '*'}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type={showPw ? 'text' : 'password'} value={form.password}
                autoComplete="new-password" style={{ flex: 1 }}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? 'Unchanged' : 'The password to store'} />
              <button type="button" className="btn btn-ghost" onClick={() => setShowPw(!showPw)}>
                {showPw ? 'Hide' : 'Show'}
              </button>
              <button type="button" className="btn btn-ghost"
                onClick={() => { setForm({ ...form, password: generatePassword() }); setShowPw(true); }}>
                Generate
              </button>
            </div>
            {strength && (
              <span className={`badge badge-${strength.color}`} style={{ marginTop: 6, display: 'inline-block' }}>
                {strength.label}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Recovery email, 2FA location, who owns this account…" />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => { setShowForm(false); setEditing(null); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Store password'}
            </button>
          </div>
        </form>
      )}

      <div className="filter-bar">
        <input type="search" value={search} placeholder="Search passwords…"
          onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 240 }} />
        <button className="btn btn-ghost" onClick={() => { setLogEntry({ id: null, title: 'All entries' }); vaultAPI.accessLog({ limit: 200 }).then(({ data }) => setLogs(data)).catch(() => {}); }}>
          📜 Full access log
        </button>
      </div>

      {loading ? <div className="page-loading">Loading…</div> : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th><th>Category</th><th>Username / ID</th>
                <th>Shared with</th><th>Last changed</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted">No passwords stored yet</td></tr>
              ) : entries.map((e) => (
                <tr key={e.id}>
                  <td>
                    <strong>{e.title}</strong>
                    {e.url && <div style={{ fontSize: 12 }}><a href={e.url} target="_blank" rel="noopener noreferrer">{e.url}</a></div>}
                  </td>
                  <td>{e.category ? <span className="badge badge-purple">{e.category}</span> : <span className="text-muted">—</span>}</td>
                  <td style={{ overflowWrap: 'anywhere' }}>{e.account_identifier || '—'}</td>
                  <td>
                    <span className={`badge badge-${e.shared_with_count > 0 ? 'blue' : 'gray'}`}>
                      {e.shared_with_count} {e.shared_with_count === 1 ? 'person' : 'people'}
                    </span>
                  </td>
                  <td>{fmtDate(e.rotated_at || e.updated_at, true)}</td>
                  <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm btn-primary" onClick={() => setRevealing(e)}>👁 Reveal</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => openAccess(e)}>Access</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => openEdit(e)}>Edit</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => openLog(e)}>Log</button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(e)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revealing && <VaultRevealModal entry={revealing} onClose={() => setRevealing(null)} />}

      {managing && (
        <div className="modal-overlay" onClick={() => setManaging(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Who can see “{managing.title}”</h3>
              <button className="modal-close" onClick={() => setManaging(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-section-title">Currently has access</div>
              {grants.length === 0 ? (
                <p className="text-muted">Nobody yet — only you can see this password.</p>
              ) : (
                <table className="data-table">
                  <tbody>
                    {grants.map((g) => (
                      <tr key={g.id}>
                        <td>
                          {g.user?.full_name || `User #${g.user_id}`}
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            since {fmtDate(g.granted_at, true)}
                            {g.expires_at ? ` · until ${fmtDate(g.expires_at, true)}` : ''}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-sm btn-danger" onClick={() => revoke(g)}>Revoke</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="form-section-title">Grant access</div>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
                {people.filter((p) => !granted.has(p.id)).map((p) => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <input type="checkbox" checked={picked.includes(p.id)} onChange={() => togglePick(p.id)} />
                    <span>{p.full_name}</span>
                    <span className="text-muted" style={{ fontSize: 12 }}>{p.role?.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Access expires on (optional)</label>
                <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
                <span className="field-hint">Leave blank for access that does not expire.</span>
              </div>
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={() => setManaging(null)}>Close</button>
                <button className="btn btn-primary" disabled={picked.length === 0} onClick={saveGrants}>
                  Grant to {picked.length || 0} {picked.length === 1 ? 'person' : 'people'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {logEntry && (
        <div className="modal-overlay" onClick={() => setLogEntry(null)}>
          <div className="modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Access log — {logEntry.title}</h3>
              <button className="modal-close" onClick={() => setLogEntry(null)}>×</button>
            </div>
            <div className="modal-body">
              {logs.length === 0 ? <p className="text-muted">Nothing recorded yet.</p> : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>When</th><th>Who</th><th>Action</th><th>Entry</th><th>IP</th></tr>
                    </thead>
                    <tbody>
                      {logs.map((l) => (
                        <tr key={l.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.timestamp)}</td>
                          <td>{l.user?.full_name || '—'}</td>
                          <td>
                            <span className={`badge badge-${
                              l.action === 'reveal' ? 'blue'
                                : l.action === 'reveal_denied' ? 'red'
                                  : l.action === 'delete' || l.action === 'revoke' ? 'orange' : 'gray'}`}>
                              {l.action.replace('_', ' ')}
                            </span>
                            {l.detail && <div className="text-muted" style={{ fontSize: 12 }}>{l.detail}</div>}
                          </td>
                          <td>{l.entry_title || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{l.ip_address || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
